import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Badge } from '@/components/ui/PageHeader';
import { businessTeam } from '@/api/business-owner';
import { useAuth } from '@/context/AuthContext';

/**
 * A business's team, by hand: the caller's own invites to help run a
 * business (accept or decline), and, for the chosen business, its team as its
 * primary owner sees it (invite a member by id, remove a manager, leave).
 * The server decides who may do what; a refusal is shown as it comes.
 */
export function BusinessTeamPanel({ providerId }: { providerId: string | null }) {
  const { user } = useAuth();
  const invites = useQuery({ queryKey: ['business-invites', user?.id], queryFn: businessTeam.myInvites, retry: false });
  const team = useQuery({
    queryKey: ['business-team', user?.id, providerId],
    queryFn: () => businessTeam.team(providerId!),
    enabled: !!providerId,
    retry: false,
  });
  const [inviteeId, setInviteeId] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  async function run(label: string, action: () => Promise<unknown>) {
    setBusy(true); setError(''); setMessage('');
    try {
      await action();
      setMessage(label);
      await Promise.all([invites.refetch(), providerId ? team.refetch() : null]);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The request failed.');
    } finally {
      setBusy(false);
    }
  }

  return <section className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4" aria-label="Business team">
    <h2 className="font-semibold">Team</h2>
    <p className="text-sm text-gray-600">An invite grants nothing until it is accepted. A manager runs their assigned location: offerings, updates, messages and the dashboard. Renting a stall, verification and the team stay with the primary owner. A decline tells nobody.</p>

    <div className="space-y-2">
      <h3 className="text-sm font-medium">Your invites</h3>
      {invites.error && <p role="alert" className="text-sm text-red-700">{invites.error.message}</p>}
      {(invites.data?.data.invites ?? []).length === 0 && !invites.isLoading && <p className="text-sm text-gray-500">No invites waiting.</p>}
      {(invites.data?.data.invites ?? []).map((invite) => <div key={invite.memberId} className="flex flex-wrap items-center gap-2 text-sm">
        <span>{invite.invitedBy?.name ?? 'Someone'} asks you to help run {invite.locations.map((l) => l.name).join(', ') || 'a business'}</span>
        <Badge variant="gray">{invite.role}</Badge>
        <Button size="sm" disabled={busy} onClick={() => void run('Accepted. You help run it from now.', () => businessTeam.respond(invite.memberId, true))}>Accept</Button>
        <Button size="sm" variant="secondary" disabled={busy} onClick={() => void run('Declined. Nobody is told.', () => businessTeam.respond(invite.memberId, false))}>Decline</Button>
      </div>)}
    </div>

    {providerId && <div className="space-y-2">
      <h3 className="text-sm font-medium">This business's team</h3>
      {team.error && <p role="alert" className="text-sm text-red-700">{team.error.message}</p>}
      {(team.data?.data.members ?? []).map((member) => <div key={member.memberId} className="flex flex-wrap items-center gap-2 text-sm">
        <span>{member.user.name}</span>
        <Badge variant={member.status === 'active' ? 'green' : 'yellow'}>{member.role} · {member.status}</Badge>
        <span className="text-gray-500">{member.locations.map((l) => l.name).join(', ')}</span>
        <Button size="sm" variant="danger" disabled={busy} onClick={() => void run(
          member.user.id === user?.id ? 'You left the team.' : 'Removed. Their access ended now.',
          () => businessTeam.remove(providerId, member.memberId),
        )}>{member.user.id === user?.id ? 'Leave' : member.status === 'invited' ? 'Withdraw invite' : 'Remove'}</Button>
      </div>)}
      <div className="flex flex-wrap gap-2 items-end">
        <label className="text-sm">Member id to invite as a manager<input className="input mt-1" value={inviteeId} onChange={(event) => setInviteeId(event.target.value.trim())} /></label>
        <Button disabled={busy || !inviteeId} onClick={() => void run('Invited. They are told, and have no access until they accept.', () => businessTeam.invite(providerId, inviteeId))}>Invite manager</Button>
      </div>
    </div>}
    {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
    {message && <p role="status" className="text-sm text-green-700">{message}</p>}
  </section>;
}
